import { Button } from '../../catalyst/button';
import { Dialog, DialogTitle, DialogDescription, DialogActions } from '../../catalyst/dialog';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import backend from '../../services/backend';

interface DeleteConfirmDialogProps {
  documentId: string;
  open: boolean;
  isPending: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export function DeleteConfirmDialog({ documentId, open, isPending, onConfirm, onClose }: DeleteConfirmDialogProps) {
  // Informational only (DR-6226): the delete always removes the pages no other
  // document uses, and keeps the shared ones.
  const cascadePreviewQuery = useQuery({
    queryKey: ['document-cascade-preview', documentId],
    queryFn: () => backend.getDocumentCascadePreview(documentId).then((r) => r.data),
    enabled: open,
  });

  const totalPages = cascadePreviewQuery.data?.pages.length ?? 0;
  const sharedPages = useMemo(
    () => cascadePreviewQuery.data?.pages.filter((p) => p.otherDocumentIds.length > 0) ?? [],
    [cascadePreviewQuery.data],
  );

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Delete Document</DialogTitle>
      <DialogDescription>
        Are you sure you want to delete this document? This action cannot be undone.
      </DialogDescription>
      <div className="mt-4 text-sm">
        {cascadePreviewQuery.isLoading && <div className="text-gray-500 dark:text-zinc-400">Checking pages…</div>}

        {cascadePreviewQuery.data && sharedPages.length === 0 && totalPages > 0 && (
          <div className="text-gray-500 dark:text-zinc-400">
            All {totalPages} page{totalPages === 1 ? '' : 's'} of this document will be deleted with it.
          </div>
        )}

        {sharedPages.length > 0 && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200">
            <div className="font-medium">
              {sharedPages.length} page{sharedPages.length === 1 ? ' is' : 's are'} shared with other documents and will
              be kept
            </div>
            <ul className="mt-2 max-h-40 overflow-y-auto space-y-1 text-xs font-mono">
              {sharedPages.slice(0, 20).map((p) => (
                <li key={p.pageId}>
                  page {p.pageIndex + 1} ({p.pageId.slice(0, 12)}…) — also in {p.otherDocumentIds.length} other document
                  {p.otherDocumentIds.length === 1 ? '' : 's'}
                </li>
              ))}
              {sharedPages.length > 20 && (
                <li className="text-amber-700 dark:text-amber-300">…and {sharedPages.length - 20} more</li>
              )}
            </ul>
          </div>
        )}
      </div>
      <DialogActions>
        <Button plain onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button color="red" onClick={onConfirm} disabled={isPending}>
          {isPending ? 'Deleting...' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
